import type { Filter, FilterDataOption, FilterDataMultiSelect } from '@/app/common/filters/core/filtersTypes.ts';
import { FILTER_TYPE } from '@/app/common/filters/core/filtersTypes.ts';
import type { DynamicFiltersResponse } from '@/app/components/search/utils/api/dynamicFiltersResponseTypes.ts';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import type { AccommodationType } from '@/app/components/search/utils/api/filtersAPIResponseTypes.ts';
import type { TranslationDictionary } from '@/app/components/translations/createTranslationDictionary.ts';
import { getTranslationServer } from '@/app/components/translations/createTranslationDictionary.ts';
import { FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { type OPTIONS_SORT, sortOptions } from '@/app/common/filters/types/shared/utils/sort.ts';
import { counterOf, disabledAtZero, keysWithSelected } from '@/app/common/filters/groups/optionCounts.ts';

const defaultPropertyTypeIds = ['3', '9', '7', '8', '1', '2', '6', '11', '14', '4', '5', '10', '12', '13'];

enum AnalyticsPropertyTypes {
  Apartments = 'Apartments',
  Villas = 'Villas',
  GuestHouses = 'Guest_houses',
  BedAndBreakfasts = 'Bed_and_Breakfasts',
  Hotels = 'Hotels',
  Resorts = 'Resorts',
  CountryHouses = 'Country_houses',
  Campsites = 'Campsites',
  Aparthotel = 'Aparthotel',
  Hostels = 'Hostels',
  Motels = 'Motels',
  BoatHotels = 'Boat_Hotels',
  Riads = 'Riads',
  Ryokans = 'Ryokans',
}

const defaultPropertyTypes = [
  {
    id: '1',
    name: 'hotels',
    translationKey: 'vacation.search.filter.accommodation.type.hotels',
    analytics: AnalyticsPropertyTypes.Hotels,
  },
  {
    id: '2',
    name: 'resorts',
    translationKey: 'vacation.search.filter.accommodation.type.resorts',
    analytics: AnalyticsPropertyTypes.Resorts,
  },
  {
    id: '3',
    name: 'apartments',
    translationKey: 'vacation.search.filter.accommodation.type.apartments',
    analytics: AnalyticsPropertyTypes.Apartments,
  },
  {
    id: '4',
    name: 'hostels',
    translationKey: 'vacation.search.filter.accommodation.type.hostels',
    analytics: AnalyticsPropertyTypes.Hostels,
  },
  {
    id: '5',
    name: 'motels',
    translationKey: 'vacation.search.filter.accommodation.type.motels',
    analytics: AnalyticsPropertyTypes.Motels,
  },
  {
    id: '6',
    name: 'countryhouses',
    translationKey: 'vacation.search.filter.accommodation.type.countryhouses',
    analytics: AnalyticsPropertyTypes.CountryHouses,
  },
  {
    id: '7',
    name: 'guesthouses',
    translationKey: 'vacation.search.filter.accommodation.type.guesthouses',
    analytics: AnalyticsPropertyTypes.GuestHouses,
  },
  {
    id: '8',
    name: 'bedandbreakfasts',
    translationKey: 'vacation.search.filter.accommodation.type.bedandbreakfasts',
    analytics: AnalyticsPropertyTypes.BedAndBreakfasts,
  },
  {
    id: '9',
    name: 'villas',
    translationKey: 'vacation.search.filter.accommodation.type.villas',
    analytics: AnalyticsPropertyTypes.Villas,
  },
  {
    id: '10',
    name: 'boatsandcruises',
    translationKey: 'vacation.search.filter.accommodation.type.boatsandcruises',
    analytics: AnalyticsPropertyTypes.BoatHotels,
  },
  {
    id: '11',
    name: 'campsites',
    translationKey: 'vacation.search.filter.accommodation.type.campsites',
    analytics: AnalyticsPropertyTypes.Campsites,
  },
  {
    id: '12',
    name: 'riads',
    translationKey: 'vacation.search.filter.accommodation.type.riads',
    analytics: AnalyticsPropertyTypes.Riads,
  },
  {
    id: '13',
    name: 'ryokans',
    translationKey: 'vacation.search.filter.accommodation.type.ryokans',
    analytics: AnalyticsPropertyTypes.Ryokans,
  },
  {
    id: '14',
    name: 'aparthotels',
    translationKey: 'vacation.search.filter.accommodation.type.aparthotels',
    analytics: AnalyticsPropertyTypes.Aparthotel,
  },
];

type CreateStarsFilterOptions = {
  selectedValues: string[];
  accommodationType?: AccommodationType;
  translationDictionary: TranslationDictionary;
};
const createPropertyTypeFilterOptions = ({
  selectedValues,
  accommodationType,
  translationDictionary,
}: CreateStarsFilterOptions): FilterDataOption[] => {
  const answered = accommodationType ? Object.keys(accommodationType) : defaultPropertyTypeIds;
  const ids = keysWithSelected(answered, selectedValues).filter(
    (id) => !!defaultPropertyTypes.find((dpt) => dpt.id === id)
  );
  return ids
    .map((id) => {
      const defaultPropertyType = defaultPropertyTypes.find((dpt) => dpt.id === id);
      const selected = selectedValues.includes(id);
      const counter = counterOf(accommodationType, id);
      return defaultPropertyType
        ? {
            label: getTranslationServer(defaultPropertyType.translationKey, translationDictionary),
            selected,
            disabled: disabledAtZero(selected, counter),
            value: id,
            counter,
          }
        : undefined;
    })
    .filter((f) => !!f);
};

type CreatePropertyTypeFilters = {
  searchParams: SearchParams;
  dynamicFiltersResponse: DynamicFiltersResponse | undefined;
  translationDictionary: TranslationDictionary;
  optionsSort?: OPTIONS_SORT;
};
export const createPropertyTypeFilters = ({
  searchParams,
  dynamicFiltersResponse,
  translationDictionary,
  optionsSort,
}: CreatePropertyTypeFilters): Filter[] => {
  const selectedValues = searchParams[URLParameters.ACCOMODATION_TYPE]?.split(',') || [];

  const rawOptions = createPropertyTypeFilterOptions({
    selectedValues,
    accommodationType: dynamicFiltersResponse?.accommodationType,
    translationDictionary,
  });
  const options = optionsSort !== undefined ? sortOptions(optionsSort, rawOptions) : rawOptions;
  const propertyTypeFilter: Filter = {
    name: FILTER_NAME.PROPERTY_TYPE,
    type: FILTER_TYPE.MULTI_SELECT,
    data: {
      options,
    } as FilterDataMultiSelect,
  };

  return [...[propertyTypeFilter]].filter((f) => !!f);
};
